/** 人物以外各种档案的 TXT:摘要 + 全部相关记录 + 原始资料附录。 */
import { historySourceLabel, historySourceNote } from './sbhistory.ts';
import { completeSchoolRoster } from './sbschool-export.ts';
import { renderProjectSettlement } from './sbproject-settlement.ts';
import { companyOriginText } from './sbcompany-origin.ts';

type Data = Record<string, any>;
export const ENTITY_TEXT_KINDS = ['firm', 'hospital', 'school', 'plot', 'project', 'unit', 'bank', 'gov', 'case', 'region', 'market', 'city'] as const;
const labels: Record<string, string> = { person: '人物', firm: '公司', hospital: '医院', school: '学校', plot: '地块', project: '楼栋', unit: '房屋', bank: '银行', gov: '政府', case: '案卷', region: '地区', market: '市场', city: '城市概况' };
export const ENTITY_APPENDIX = '原始完整资料附录（保留字段、编号与原始单位）\n';
const known = (x: unknown) => x == null ? '未知' : typeof x === 'object' ? JSON.stringify(x) : String(x);
const validId = (x: unknown): x is number => typeof x === 'number' && Number.isSafeInteger(x) && x >= 0 && x <= 0xffffffff;
const identity = (x: unknown) => validId(x) ? String(x) : '未知';
const count = (x: unknown) => typeof x === 'number' && Number.isFinite(x) && x >= 0 ? String(x) : '未知';
const date = (x: unknown) => validId(x) ? `第 ${Math.floor(x / 360) + 1} 年 ${Math.floor(x % 360 / 30) + 1} 月 ${x % 30 + 1} 日（游戏日 ${x}）` : '未知';
const yuan = (x: unknown) => typeof x === 'number' && Number.isFinite(x) ? `${x.toFixed(2)} 元` : '未知';
const ref = (x: any, kind?: string) => x == null || typeof x !== 'object' || Array.isArray(x) ? '未知' : `${typeof x.name === 'string' && x.name.trim() ? x.name : '名称未知'} · ${labels[x.kind ?? kind] ?? x.kind ?? kind ?? '对象'} #${identity(x.id)}`;

function summary(x: Data): string[] {
  const d = x.data ?? {}, kind = x.kind;
  const lines = ['当前资料'];
  if (kind === 'firm') lines.push(...companyOriginText(d.origin), `类型 ${known(d.kind)} · 状态 ${d.closed === true ? '已关闭' : d.closed === false ? '经营中' : '未知'}`, `现金 ${yuan(d.balance?.cash)} · 当前估值 ${yuan(d.value)} · 员工数 ${Array.isArray(d.staff) ? d.staff.length : '未知'}`, '员工数不含老板。');
  else if (kind === 'hospital') lines.push(`医院 ${ref(d.hospital, 'hospital')} · 实际运营公司 ${ref(d.provider, 'firm')}`, `当前医生 ${count(d.capacity?.doctors)} · 专家 ${count(d.capacity?.specialists)} · 床位 ${count(d.capacity?.beds)} · 实际占床 ${count(d.ward?.occupied)}`, `当日医疗实收 ${yuan(d.dayReceived)} · 累计医疗实收 ${yuan(d.received)} · 累计耗材采购 ${yuan(d.suppliesPurchased)}`);
  else if (kind === 'school') lines.push(`学校 ${ref(d.school, 'school')} · 办学层次 ${known(d.level)} · 设施等级 ${count(d.grade)}`, `课堂实际采样日 ${date(d.sampleDay)} · 座位 ${count(d.seats)} · 想就读 ${count(d.wanted)} · 候补或暂未到 ${count(d.waiting)}`, `教师可用工时 ${count(d.teacherHours)} · 已预留 ${count(d.reservedHours)} · 真实授课 ${count(d.taughtHours)}`);
  else if (kind === 'project') {
    lines.push(`固定楼栋 #${identity(x.id)} · 地块 #${identity(d.plot)} · 楼型 ${known(d.type)} · 楼层 ${count(d.floors)}`, `当前施工阶段 ${known(d.stage)} · 发包日 ${date(d.posted)} · 完工日 ${date(d.finished)}`);
    const settlement = renderProjectSettlement(d.settlement, (label, value) => `${label} ${value}\n`);
    if (settlement !== null) lines.push(settlement.trimEnd(), `剩余成本估算 ${yuan(d.remaining)}`);
    else lines.push(`监管账户 ${yuan(d.escrow)} · 合同价 ${yuan(d.price)} · 剩余成本 ${yuan(d.remaining)}`);
  }
  else if (kind === 'unit') lines.push(`固定房屋 #${identity(x.id)} · 地块 #${identity(d.plot)} · 原楼栋 ${ref(d.building, 'project')}`, `买入支付 ${yuan(d.bought)} · 挂牌状态 ${d.brief?.forSale === false ? '当前未挂牌' : d.brief?.forSale === true ? `已挂牌 · 当前要价 ${yuan(d.saleAsk)}` : '未知'}`);
  else if (kind === 'plot') lines.push(`固定地块 #${identity(x.id)} · 当前主人 ${ref(d.land?.owner)}`, `当前挂牌要价 ${yuan(d.land?.ask)} · 原取得支付 ${yuan(d.land?.paid)} · 取得日 ${date(d.land?.since)}`);
  else if (kind === 'bank') lines.push(`银行 #${identity(x.id)} · 运营公司 ${ref(d.company?.firm, 'firm')}`, `当前现钱 ${yuan(d.cash)} · 客户存款 ${yuan(d.deposits)} · 在贷本金 ${yuan(d.lent)} · 当前权益 ${yuan(d.equity)}`);
  else if (kind === 'gov') lines.push(`国库现钱与存款合计 ${yuan(d.treasury)} · 当前现钱 ${yuan(d.fisc?.cash)} · 存银行 ${yuan(d.fisc?.dep)}`);
  else if (kind === 'case') lines.push(`实际案号 #${identity(x.id)} · 案件类型 ${known(d.kind)} · 当前阶段 ${known(d.stage)} · 事件日 ${date(d.day)}`, `金额 ${yuan(d.amount)} · 判给 ${yuan(d.award)}`, '附录里 wageRecovery 的金额单位是分。');
  else if (kind === 'region') lines.push(`实际地区 #${identity(x.id)} · 名称 ${known(d.name)} · 当前人口 ${count(d.pop)}`, `日工钱 ${yuan(d.wage)} · 地区货币余额 ${yuan(d.money)}`);
  else if (kind === 'market') lines.push(`实际市场 #${identity(x.id)} · 名称 ${known(d.name)}`, `当前价 ${known(d.price)} · 最近成交价 ${known(d.last)} · 成交量 ${known(d.volume)}`);
  else if (kind === 'city') lines.push(`地图 ${count(d.w)} × ${count(d.h)} · 概况采样日 ${date(d.statsDay)} · 当前人口 ${count(d.stats?.pop)} · 公司数 ${count(d.stats?.firms)}`);
  else lines.push('见附录。');
  return lines;
}

/** 每 64 条记录让一下(下载时可取消、不卡页面)。 */
export function* entityTextSteps(x: Data): Generator<void, string[], unknown> {
  const archive = x.archive ?? {}, hasHistory = Array.isArray(x.history), records: any[] = hasHistory ? x.history : [];
  const body: string[] = [], rawRecords: string[] = [], counts = new Map<string, number>();
  const schoolRoster = x.kind === 'school' ? completeSchoolRoster(x.data, x.day, x.id) : null;
  const schoolLines: string[] = [], rawStudents: string[] = [];
  if (schoolRoster) {
    const currentAttendance = schoolRoster.enabled === true && schoolRoster.sampleDay === schoolRoster.day;
    const came = currentAttendance ? '当天到课' : '上次排课到课';
    schoolLines.push('在校学生名册（按居民编号）',
      `名册日 ${date(schoolRoster.day)} · 上次排课 ${date(schoolRoster.sampleDay)}`,
      `共 ${schoolRoster.total} 人 · ${came} ${schoolRoster.present} 人 · 没到 ${schoolRoster.absent} 人`);
    for (let i = 0; i < schoolRoster.students.length; i++) {
      const r = schoolRoster.students[i];
      schoolLines.push(`学生 ${ref(r.who, 'person')} · 学段 ${known(r.stage)} · 班号 ${identity(r.classNo)} · 入学日 ${date(r.since)}`,
        `${came} ${r.present ? '是' : '否'} · 课时 ${count(r.hours)} 小时 · 等待原因 ${r.waiting === '' ? '无' : known(r.waiting)}`,
        `累计到课 ${count(r.attended)} 天 · 有效学习 ${count(r.learning)} 学习日 · 课程要求 ${count(r.required)} 学习日`);
      // 附录的学生数组也在这一遍里逐行序列化,好让一下
      rawStudents.push((i ? ',\n' : '') + JSON.stringify(r, null, 2).replace(/^/gm, '        '));
      if ((i + 1) % 64 === 0) yield;
    }
  }
  for (let i = 0; i < records.length; i++) {
    const r = records[i], source = typeof r?.source === 'string' ? r.source : '未知来源';
    counts.set(source, (counts.get(source) ?? 0) + 1);
    body.push(`\n记录 #${known(r?.seq)} · ${historySourceLabel(source)} [${source}] · 事件日 ${date(r?.day)}\n主实体 ${known(r?.kind)} #${identity(r?.entityId)}\n关联对象：${JSON.stringify(r?.refs ?? null)}\n记录内容：\n${JSON.stringify(r?.data, null, 2) ?? '未知'}\n`);
    rawRecords.push((i ? ',\n' : '') + JSON.stringify(r, null, 2).replace(/^/gm, '    '));
    if ((i + 1) % 64 === 0) yield;
  }
  const lines = [`${known(x.name)} · ${labels[x.kind] ?? known(x.kind)}档案（TXT）`, `对象 ${known(x.kind)} #${identity(x.id)} · 城市种子 ${known(x.seed)} · 导出于 ${date(x.day)}`, '',
    '资料范围', ...(x.recordScope ? [x.recordScope] : []), ...(archive.note ? [archive.note] : []),
    `归档状态 ${known(archive.state)} · 真实保存起点 ${date(archive.coverageStart)} · 保存到完整日 ${date(archive.completeThroughDay)}`,
    `仅保留起点后/现有资料 ${known(archive.retainedOnly)} · 是否持久保存 ${known(archive.durable)} · 已放弃记录数 ${known(archive.discarded)}`,
    '',
    ...summary(x), ...(schoolLines.length ? ['', ...schoolLines] : []), '', '本次导出已保存的相关记录', hasHistory ? `共 ${records.length} 条` : '没有归档记录',
    ...[...counts].flatMap(([source, n]) => [`${historySourceLabel(source)} [${source}]：${n} 条。${historySourceNote(source)}`]), '',
    '全部相关记录（按归档序号）'];
  const parts = [lines.join('\n') + '\n', ...body, '\n' + ENTITY_APPENDIX, '{\n'];
  const entries = Object.entries(x).filter(([, value]) => value !== undefined && typeof value !== 'function' && typeof value !== 'symbol');
  for (let i = 0; i < entries.length; i++) {
    const [key, value] = entries[i];
    if (i) parts.push(',\n');
    if (key === 'history' && Array.isArray(value)) {
      parts.push(`  ${JSON.stringify(key)}: `, value.length ? '[\n' : '[]');
      for (const raw of rawRecords) parts.push(raw);
      if (value.length) parts.push('\n  ]');
    }
    else if (key === 'data' && schoolRoster) {
      parts.push('  "data": {\n');
      const fields = Object.entries(value as Data).filter(([, v]) => v !== undefined && typeof v !== 'function' && typeof v !== 'symbol');
      for (let j = 0; j < fields.length; j++) {
        const [field, item] = fields[j];
        if (j) parts.push(',\n');
        if (field !== 'studentRoster') parts.push(`    ${JSON.stringify(field)}: ${JSON.stringify(item, null, 2).replace(/\n/g, '\n    ')}`);
        else {
          parts.push('    "studentRoster": {\n');
          const rosterFields = Object.entries(schoolRoster).filter(([, v]) => v !== undefined && typeof v !== 'function' && typeof v !== 'symbol');
          for (let k = 0; k < rosterFields.length; k++) {
            const [name, v] = rosterFields[k];
            if (k) parts.push(',\n');
            if (name === 'students') {
              parts.push('      "students": ', rawStudents.length ? '[\n' : '[]');
              for (const row of rawStudents) parts.push(row);
              if (rawStudents.length) parts.push('\n      ]');
            } else parts.push(`      ${JSON.stringify(name)}: ${JSON.stringify(v, null, 2).replace(/\n/g, '\n      ')}`);
          }
          parts.push('\n    }');
        }
      }
      parts.push('\n  }');
    }
    else parts.push(`  ${JSON.stringify(key)}: ${JSON.stringify(value, null, 2).replace(/\n/g, '\n  ')}`);
  }
  parts.push('\n}');
  return parts;
}

export function entityText(x: Data): string {
  const steps = entityTextSteps(x);
  for (;;) { const next = steps.next(); if (next.done) return next.value.join(''); }
}


export interface ExportRequest { id: number; gen: number; kind: string; entityId: number; format: string }
export interface ExportReply { id: number; gen: number; raw: string; error?: string; kind?: string; name?: string }
type YieldTask = (signal: AbortSignal) => Promise<void>;

/** MessageChannel让出任务；不依赖暂停页可能不运行的RAF。并不拆分JSON.parse或单条JSON.stringify。 */
export const yieldExportTask: YieldTask = signal => new Promise((resolve, reject) => {
  const channel = new MessageChannel();
  const clean = () => { channel.port1.close(); channel.port2.close(); signal.removeEventListener('abort', abort); };
  const abort = () => { clean(); reject(new Error('下载已取消')); };
  if (signal.aborted) { abort(); return; }
  signal.addEventListener('abort', abort, { once: true });
  channel.port1.onmessage = () => { clean(); resolve(); };
  try { channel.port2.postMessage(0); } catch (error) { clean(); reject(error); }
});

/** 点击时确定唯一最新请求；换城、离页及下一请求取消旧转换。Worker导出协议保持不变。 */
export function createExportDownloads(options: {
  currentGen(): number;
  specialText(data: Data): string;
  personSteps?(data: Data): Generator<void, string, unknown>;
  filename(reply: ExportReply, data: Data, format: string): string;
  ready(blob: Blob, filename: string, format: string): void;
  failed(reason: string): void;
  yieldTask?: YieldTask;
}) {
  let active: { request: ExportRequest; controller: AbortController; received: boolean } | null = null;
  const reset = () => { active?.controller.abort(); active = null; };
  const begin = (request: ExportRequest): boolean => {
    reset();
    if (!Number.isSafeInteger(request.id) || request.id < 0 || !Number.isSafeInteger(request.gen) || request.gen !== options.currentGen() ||
        !validId(request.entityId) || !['person', ...ENTITY_TEXT_KINDS].includes(request.kind) || !['json', 'txt'].includes(request.format)) {
      options.failed('下载请求的对象、编号或格式无效'); return false;
    }
    active = { request: { ...request }, controller: new AbortController(), received: false }; return true;
  };
  const receive = async (reply: ExportReply): Promise<void> => {
    const work = active;
    if (!work || work.received || work.request.id !== reply.id || work.request.gen !== reply.gen || reply.gen !== options.currentGen()) return;
    work.received = true;
    const current = () => active === work && !work.controller.signal.aborted && reply.gen === options.currentGen();
    const giveControl = async () => {
      if (!current()) return false;
      await (options.yieldTask ?? yieldExportTask)(work.controller.signal);
      return current();
    };
    try {
      if (!current()) return;
      if (reply.error) throw new Error(reply.error);
      const format = work.request.format;
      // TXT先提供一次取消机会；解析、公司现有格式化、单条大记录及最终整体序列化仍是同步工作。
      if (format === 'txt' && !await giveControl()) return;
      if (!current()) return;
      const data = JSON.parse(reply.raw);
      if (data == null || typeof data !== 'object' || Array.isArray(data) || data.kind !== work.request.kind || data.id !== work.request.entityId ||
          (reply.kind != null && reply.kind !== work.request.kind)) throw new Error('返回档案与请求对象不一致');
      let parts: string[];
      if (format === 'json') parts = [JSON.stringify(data, null, 2)];
      else if (data.kind === 'person' && options.personSteps) {
        const steps = options.personSteps(data);
        for (;;) {
          if (!current()) return;
          const next = steps.next();
          if (next.done) { parts = ['\ufeff', next.value]; break; }
          if (!await giveControl()) return;
        }
      }
      else if (data.kind === 'person' || data.kind === 'firm') parts = ['\ufeff', options.specialText(data)];
      else {
        const steps = entityTextSteps(data);
        for (;;) {
          if (!current()) return;
          const next = steps.next();
          if (next.done) { parts = ['\ufeff', ...next.value]; break; }
          if (!await giveControl()) return;
        }
      }
      if (!current()) return;
      const filename = options.filename(reply, data, format);
      const blob = new Blob(parts, { type: format === 'txt' ? 'text/plain;charset=utf-8' : 'application/json;charset=utf-8' });
      if (current()) options.ready(blob, filename, format);
    } catch (error) {
      if (current()) options.failed(error instanceof Error ? error.message : String(error));
    } finally {
      if (active === work) active = null;
    }
  };
  return { begin, receive, reset };
}
